import { type TypescriptHelpersT } from '@elliemae/ds-typescript-helpers';
import React, { useCallback } from 'react';
import type { ControlledDateTimePickerContextT } from '../../config/useControlledDateTimePicker.js';
import { ControlledDateTimePickerDatatestid } from '../../constants/index.js';
import type { DSControlledDateTimePickerInternalsT } from '../../sharedTypes.js';
import { StyledDateInput } from '../Styleds.js';
import { announcableCardinalDays } from '../../utils/constants.js';
import { convertToPositiveNumberIfPossible } from '../../utils/numberHelpers.js';

const regExpNumbers = /^[0-9]+$/;
const isInvalidDay = (day: string) => {
  if (day === '') return false;
  const numberOnly = regExpNumbers.test(day);
  return day.length > 2 || day === '00' || !numberOnly || Number.parseInt(day, 10) > 31;
};

interface DDInputsPropsT {
  onChange: DSControlledDateTimePickerInternalsT.OnChangeFunc;
  onBlur: DSControlledDateTimePickerInternalsT.OnBlurFunc;
  onFocus: DSControlledDateTimePickerInternalsT.OnFocusFunc;
  onKeyDown: DSControlledDateTimePickerInternalsT.OnKeyDown;
  value: string;
  disabled: boolean;
  applyAriaDisabled?: boolean;
  hasError: boolean;
  placeholder: string;
  contextRef: React.MutableRefObject<HTMLInputElement | null>;
  tabIndex?: TypescriptHelpersT.WCAGTabIndex;
  isFocused: boolean;
  getProps: ControlledDateTimePickerContextT['getProps'];
  readOnly: boolean;
}
export const DDInput = React.memo(
  ({
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    value,
    disabled,
    applyAriaDisabled,
    hasError,
    contextRef,
    tabIndex,
    placeholder,
    isFocused,
    getProps,
    readOnly,
  }: DDInputsPropsT): JSX.Element => {
    const validateOnChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (isInvalidDay(e?.currentTarget?.value)) {
          e.preventDefault();
          return;
        }
        onChange(e);
      },
      [onChange],
    );
    const dayNum = convertToPositiveNumberIfPossible(value) as DSControlledDateTimePickerInternalsT.DateDay | -1;

    return (
      <StyledDateInput
        onChange={validateOnChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        value={value}
        innerRef={(DomElem: HTMLInputElement) => {
          (contextRef as React.MutableRefObject<HTMLInputElement>).current = DomElem;
          if (isFocused) DomElem?.focus?.();
        }}
        tabIndex={tabIndex}
        placeholder={placeholder}
        aria-label="day"
        data-testid={ControlledDateTimePickerDatatestid.DATE_INPUTS.DAY}
        disabled={disabled}
        aria-disabled={applyAriaDisabled}
        aria-invalid={hasError}
        getOwnerProps={getProps}
        readOnly={readOnly}
        role="spinbutton"
        aria-valuemin={1}
        aria-valuemax={31}
        // when empty, valuenow falls back to the min (not omitted) so VoiceOver honors
        // aria-valuetext="empty" instead of announcing a computed position percentage
        aria-valuenow={dayNum === -1 ? 1 : dayNum}
        aria-valuetext={dayNum === -1 ? 'empty' : announcableCardinalDays[dayNum]}
      />
    );
  },
);
